import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { format } from "date-fns";
import { OrderItem } from "utils/types";

const OrderRow = (
  props: OrderItem & { timeZone: string; onDoubleClick?: () => void; isNew?: boolean }
) => {
  const paymentType = String(props.paymentType || "").trim() || "Pay at Counter";
  const customerName = [
    (props as any)?.customer?.firstName ?? (props as any)?.firstName,
    (props as any)?.customer?.lastName ?? (props as any)?.lastName,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

  const formatDate = (value: any) => {
    const parsed = new Date(String(value || ""));
    return Number.isNaN(parsed.getTime()) ? String(value || "--") : format(parsed, "MM/dd/yyyy");
  };

  const formatTime = (value: any) => {
    const raw = String(value || "").trim();
    if (!raw) return "--";

    const parsed = new Date(`2000-01-01T${raw}`);
    return Number.isNaN(parsed.getTime()) ? raw : format(parsed, "HH:mm:ss");
  };

  const orderDateTime = String(props.onDateTime || "").trim();
  const parsedOrderDateTime = orderDateTime ? new Date(orderDateTime) : null;
  const [date, time] =
    parsedOrderDateTime && !Number.isNaN(parsedOrderDateTime.getTime())
      ? [format(parsedOrderDateTime, "MM/dd/yyyy"), format(parsedOrderDateTime, "HH:mm:ss")]
      : [formatDate(props.onDate), formatTime(props.onTime)];

  const scheduledDate = formatDate(props.onDate);
  const scheduledTime = formatTime(props.onTime);
  const isScheduledSameDay = scheduledDate === date;
  const isTakeOut = String(props.orderType || "").toLowerCase().includes("take");
  const scheduleLabel = isTakeOut ? "Pickup" : "Delivery";
  const typeChipClass = isTakeOut
    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-200"
    : "bg-primary/10 text-primary";
  const scheduleChipClass = isScheduledSameDay
    ? isTakeOut
      ? "border-sky-200/80 bg-sky-50/80 text-sky-800 shadow-[0_10px_26px_rgba(14,165,233,0.1)]"
      : "border-blue-200/80 bg-blue-50/80 text-blue-800 shadow-[0_10px_26px_rgba(37,99,235,0.1)]"
    : "border-red-300/80 bg-red-50/75 text-red-700 shadow-[0_0_0_1px_rgba(248,113,113,0.2),0_12px_30px_rgba(239,68,68,0.16)] animate-[scheduleAlert_1.9s_ease-in-out_infinite]";

  return (
    <div
      className={`grid grid-cols-[90px_minmax(180px,1.2fr)_120px_130px_110px_210px_170px_120px] items-center gap-4 rounded-xl border px-4 py-3 text-sm shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md ${
        props.isNew
          ? "dcs-new-order-blink border-rose-300 bg-white"
          : "border-gray-200 bg-white"
      }`}
      onDoubleClick={props.onDoubleClick}
    >
      <p className="font-bold text-gray-950">#{props.orderID}</p>
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-semibold text-gray-900">
          <PersonRoundedIcon className="!text-[18px] text-gray-400" />
          <span className="truncate">{customerName || "Walk-in Customer"}</span>
        </div>
        {!!props.customerID && (
          <p className="pl-6 text-xs text-gray-500">Customer #{props.customerID}</p>
        )}
      </div>
      <p>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${typeChipClass}`}>
          {props.orderType || "--"}
        </span>
      </p>
      <p className="flex items-center gap-2 font-medium text-gray-700">
        <CalendarMonthRoundedIcon className="!text-[17px] text-gray-400" />
        {date || "--"}
      </p>
      <p className="flex items-center gap-2 font-medium text-gray-700">
        <ScheduleRoundedIcon className="!text-[17px] text-gray-400" />
        {time || "--"}
      </p>
      <div
        className={`w-fit rounded-lg border px-2.5 py-1.5 text-xs font-semibold leading-5 backdrop-blur-sm ${scheduleChipClass}`}
      >
        <span className="block text-[10px] font-semibold uppercase leading-4 opacity-75">
          {scheduleLabel}
        </span>
        <span className="font-semibold text-current">
          {scheduledDate} {scheduledTime}
        </span>
      </div>
      <p className="flex items-center gap-2 font-medium text-gray-700">
        <CreditCardRoundedIcon className="!text-[17px] text-gray-400" />
        <span className="truncate">{paymentType}</span>
      </p>
      <p className="flex items-center justify-end gap-1 font-bold text-gray-950">
        <AttachMoneyRoundedIcon className="!text-[18px] text-green-600" />
        {Number(props.orderTotal || 0).toFixed(2)}
      </p>
    </div>
  );
};

export default OrderRow;
